'use client'
import clsx from 'clsx'
import Icon from '../Icon/Icon'
import { Icons } from '../Icon/constants'
import { useTranslation } from '../../context/ConfigContext'
import { useEffect } from 'react'

type Props = {
  id: string
  anchor?: 'left' | 'right'
  title?: string
  classes?: {
    dialog?: string
    header?: string
    panel?: string
  }
  children: React.ReactNode
  isTransitioning: { current: boolean }
  open: boolean
  onClose(): void
}
export function MessikaDrawer(props: Props) {
  const { id, anchor = 'right', title, classes, children, open, onClose, isTransitioning } = props
  const ns = id
  const t = useTranslation()

  useEffect(() => {
    if (!open) {
      return
    }
    const overlayEl = document.getElementById(`${ns}-overlay`) as HTMLDivElement
    const handleKeydown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        onClose()
      }
    }
    const handleOverlayClick = () => {
      if (isTransitioning.current) {
        return
      }
      onClose()
    }
    document.addEventListener('keydown', handleKeydown)
    overlayEl.addEventListener('click', handleOverlayClick)
    return () => {
      document.removeEventListener('keydown', handleKeydown)
      overlayEl.removeEventListener('click', handleOverlayClick)
    }
  }, [open])

  useEffect(() => {
    const dialogEl = document.getElementById(`${ns}-dialog`) as HTMLDialogElement
    const handleAriaExpand = (ev: MouseEvent) => {
      const target = (ev.target as HTMLElement).closest('[aria-controls]')
      if (!target || !dialogEl.contains(target)) {
        return
      }
      const ariaControls = target.getAttribute('aria-controls')
      if (!ariaControls) {
        return
      }
      const controlledEl = document.getElementById(ariaControls)
      if (!controlledEl) {
        return
      }
      if (controlledEl === dialogEl) {
        onClose()
        return
      }
      const ariaExpand = target.getAttribute('aria-expanded') === 'true'
      const newExpand = !ariaExpand
      const newHidden = !newExpand
      controlledEl.ariaHidden = newHidden ? 'true' : 'false'

      const controlElList = document.querySelectorAll(`[aria-controls="${ariaControls}"]`)
      for (const el of controlElList) {
        el.ariaExpanded = newExpand ? 'true' : 'false'
      }
    }

    dialogEl.addEventListener('click', handleAriaExpand)
    return () => {
      dialogEl.removeEventListener('click', handleAriaExpand)
    }
  }, [open])

  useEffect(() => {
    if (isTransitioning.current) return
    isTransitioning.current = true
    const panelEl = document.getElementById(`${ns}-panel`) as HTMLDivElement
    const dialogEl = document.getElementById(`${ns}-dialog`) as HTMLDialogElement
    const overlayEl = document.getElementById(`${ns}-overlay`) as HTMLDivElement
    if (open) {
      dialogEl.open = open
      setTimeout(() => {
        document.body.classList.add('overflow-hidden')
        overlayEl.ariaHidden = 'false'
        panelEl.ariaHidden = 'false'
        isTransitioning.current = false
      }, 30)
    } else {
      document.body.classList.remove('overflow-hidden')
      overlayEl.ariaHidden = 'true'
      panelEl.ariaHidden = 'true'
      setTimeout(() => {
        dialogEl.open = false
        isTransitioning.current = false
      }, 300)
    }
  }, [open])

  return (
    <div id={ns}>
      <dialog
        id={`${ns}-dialog`}
        className={classes?.dialog || `fixed top-0 start-0 w-full h-full z-40 bg-transparent`}>
        <div className='relative flex w-full h-full'>
          <div
            id={`${ns}-overlay`}
            aria-hidden='false'
            className='absolute w-full h-full aria-hidden:bg-black/0 bg-black/60 transition-all duration-300 hidden lg:block flex-grow'></div>
          <div
            aria-hidden='true'
            id={`${ns}-panel`}
            className={clsx(
              classes?.panel ||
                'bg-white w-full lg:min-w-[496px] h-full overflow-hidden transition-all duration-300 flex flex-col flex-none lg:w-1/3',
              {
                'ltr:aria-hidden:translate-x-full rtl:aria-hidden:-translate-x-full translate-x-0 ms-auto':
                  anchor === 'right',
                'ltr:aria-hidden:-translate-x-full rtl:aria-hidden:translate-x-full translate-x-0':
                  anchor === 'left',
              },
            )}>
            <header
              className={
                classes?.header || 'pt-3 pb-8 px-3 lg:p-8 flex-none relative order-1 lg:order-0'
              }>
              <div className='h-6 w-full relative z-10'>
                {title && (
                  <div className='hidden lg:block sm:text-h5 text-h5 uppercase font-medium'>
                    {title}
                  </div>
                )}
                <button
                  aria-controls={`${ns}-dialog`}
                  aria-expanded='false'
                  className={`${ns}-close-btn absolute top-0 lg:start-auto lg:end-2 gap-2 uppercase inline-flex text-p2-sm lg:text-p2 items-center`}>
                  {t('Close')}
                  <Icon iconName={Icons.CLOSE} className='size-6' />
                </button>
              </div>
            </header>
            <div className='flex-grow overflow-auto order-0 lg:order-1' id={`${ns}-content`}>
              {title && (
                <div className='lg:hidden text-h5 uppercase font-medium px-4 pt-8 pb-6'>
                  {title}
                </div>
              )}
              {children}
            </div>
          </div>
        </div>
      </dialog>
    </div>
  )
}
